/**
 * UploadPage — route page for /ingest/upload.
 *
 * Flow:
 *   1. User drops / selects files via DropZone
 *   2. "Start Processing" triggers:
 *      a. POST /api/ingest/batches → batchId
 *      b. Upload each file via POST /api/uploads (< 100 MB path)
 *      c. POST /api/ingest/batches/:id/items with uploadId metadata
 *   3. BatchProgress subscribes to SSE stream and shows per-file state
 *   4. On completion, a summary is shown with a "Start New Batch" action
 *
 * Error handling:
 *   - Batch creation failure: inline error, no upload started
 *   - Per-file upload failure: file marked failed locally, rest continue
 *   - SSE error: handled inside BatchProgress
 */

import { useState, useCallback, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { CheckCircle2, AlertTriangle, ArrowRight } from 'lucide-react'
import { cn } from '../../../lib/utils'
import { useAppStore } from '../../../stores/app-store'
import { useAuth } from '../../../hooks/use-auth'
import { DropZone } from './DropZone'
import { BatchProgress } from './BatchProgress'
import { PackageContextBar } from './package-context-bar'
import type { PackageContext } from './package-context-bar'
import { IngestBreadcrumb } from '../../../components/layout/ingest-breadcrumb'
import { ingestApiClient } from '../ingest-api-client'

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

type PagePhase = 'select' | 'uploading' | 'processing' | 'done'

interface CompletionSummary {
  ready: number
  failed: number
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function UploadPage() {
  const { t } = useTranslation('ingest')
  const { theme } = useAppStore()
  const navigate = useNavigate()
  const isDark = theme === 'dark'
  const { tenantId } = useAuth()

  // GH-252: package context anchored to this upload session.
  // null = user has not yet chosen a package OR clicked Skip.
  // false = user explicitly clicked "Skip — standalone batch".
  const [packageContext, setPackageContext] = useState<PackageContext | null>(null)
  const [isStandalone, setIsStandalone] = useState(false)

  const [phase, setPhase] = useState<PagePhase>('select')
  const [files, setFiles] = useState<File[]>([])
  const [batchId, setBatchId] = useState<string | null>(null)
  const [uploadError, setUploadError] = useState<string | null>(null)
  const [uploadProgress, setUploadProgress] = useState<{ done: number; total: number }>({
    done: 0,
    total: 0,
  })
  const [summary, setSummary] = useState<CompletionSummary | null>(null)
  // Expected file count for the active batch. Lives in state so we can restore
  // the BatchProgress view across navigation — without it, re-mounting after
  // returning to /ingest/upload would render a 0/0 progress bar.
  const [expectedCount, setExpectedCount] = useState(0)

  // Restore in-flight batch state when the user navigates back to /ingest/upload.
  // Without this the page resets to "Drop files here" even though a batch is
  // still processing — the user loses all visibility until the batch finishes.
  useEffect(() => {
    if (phase !== 'select') return
    const stored = window.localStorage.getItem('agentwiki-active-batch')
    if (!stored) return
    try {
      const parsed = JSON.parse(stored) as { batchId: string; expectedCount: number; ts: number }
      // Only auto-restore if the batch is < 24h old; older = stale state to drop.
      if (Date.now() - parsed.ts > 24 * 60 * 60 * 1000) {
        window.localStorage.removeItem('agentwiki-active-batch')
        return
      }
      setBatchId(parsed.batchId)
      setExpectedCount(parsed.expectedCount)
      setPhase('processing')
    } catch {
      window.localStorage.removeItem('agentwiki-active-batch')
    }
  }, [phase])

  // -------------------------------------------------------------------------
  // File queue management
  // -------------------------------------------------------------------------

  const handleFilesAccepted = useCallback((incoming: File[]) => {
    setFiles((prev) => {
      // Deduplicate by name + size
      const existing = new Set(prev.map((f) => `${f.name}-${f.size}`))
      const novel = incoming.filter((f) => !existing.has(`${f.name}-${f.size}`))
      return [...prev, ...novel]
    })
  }, [])

  const handleRemoveFile = useCallback((index: number) => {
    setFiles((prev) => prev.filter((_, i) => i !== index))
  }, [])

  // -------------------------------------------------------------------------
  // Upload flow
  // -------------------------------------------------------------------------

  const handleStartProcessing = async () => {
    if (files.length === 0) return
    setUploadError(null)
    setPhase('uploading')
    setUploadProgress({ done: 0, total: files.length })

    // Step 1: create batch — include package context if set (GH-252)
    let createdBatchId: string
    try {
      const batchPayload = packageContext
        ? { packageId: packageContext.packageId, sourceGroup: packageContext.sourceGroup }
        : undefined
      const res = await ingestApiClient.createBatch(batchPayload)
      createdBatchId = res.batchId
      setBatchId(createdBatchId)
    } catch (err) {
      const msg = err instanceof Error ? err.message : t('errors.upload_failed')
      setUploadError(msg)
      setPhase('select')
      return
    }

    // Step 2: upload each file and collect upload IDs
    const uploads: { uploadId: string; filename: string; mime: string; size: number }[] = []

    for (let i = 0; i < files.length; i++) {
      const file = files[i]
      try {
        const res = await ingestApiClient.uploadFile(file)
        uploads.push({
          uploadId: res.uploadId,
          filename: file.name,
          mime: file.type,
          size: file.size,
        })
      } catch {
        // Mark individual file failed by adding a placeholder that the backend
        // will not find — the SSE stream will emit item.failed for it.
        // We still want the batch to continue for other files.
        uploads.push({
          uploadId: `__failed__${i}`,
          filename: file.name,
          mime: file.type,
          size: file.size,
        })
      } finally {
        setUploadProgress((p) => ({ ...p, done: p.done + 1 }))
      }
    }

    // Step 3: register items with batch
    try {
      await ingestApiClient.addBatchItems(createdBatchId, { uploads })
    } catch (err) {
      const msg = err instanceof Error ? err.message : t('errors.upload_failed')
      setUploadError(msg)
      setPhase('select')
      return
    }

    // Step 4: hand off to BatchProgress (SSE stream)
    setExpectedCount(files.length)
    window.localStorage.setItem(
      'agentwiki-active-batch',
      JSON.stringify({ batchId: createdBatchId, expectedCount: files.length, ts: Date.now() }),
    )
    setPhase('processing')
  }

  const handleBatchComplete = useCallback((counts: CompletionSummary) => {
    setSummary(counts)
    setPhase('done')
    window.localStorage.removeItem('agentwiki-active-batch')
  }, [])

  const handleStartNew = useCallback(() => {
    setFiles([])
    setBatchId(null)
    setSummary(null)
    setUploadError(null)
    setExpectedCount(0)
    setPhase('select')
    setUploadProgress({ done: 0, total: 0 })
    // GH-252: reset package context so the bar re-appears on next upload
    setPackageContext(null)
    setIsStandalone(false)
    window.localStorage.removeItem('agentwiki-active-batch')
  }, [])

  // -------------------------------------------------------------------------
  // Render
  // -------------------------------------------------------------------------

  return (
    <div className={cn('min-h-screen', isDark ? 'bg-surface-0' : 'bg-neutral-50')}>
      <IngestBreadcrumb
        steps={[
          { label: 'Workspace', to: '/' },
          { label: 'Ingest pipeline' },
          { label: phase === 'select' ? 'Upload' : phase === 'uploading' ? 'Uploading' : phase === 'processing' ? 'Processing' : 'Done' },
        ]}
        trailing={batchId && (
          <span className={cn('font-mono text-xs', isDark ? 'text-neutral-500' : 'text-neutral-500')} title={batchId}>
            #{batchId.slice(0, 6)}…
          </span>
        )}
      />

      {/* GH-252: Package context bar — sticky above the drop zone */}
      {(phase === 'select') && !isStandalone && (
        <PackageContextBar
          tenantId={tenantId}
          onContextChange={setPackageContext}
          onSkip={() => {
            setPackageContext(null)
            setIsStandalone(true)
          }}
        />
      )}

      {/* Body — `data-tour="upload-context"` anchors the onboarding tour spotlight
          to the entire upload workflow region (package context bar + dropzone +
          actions), not just the conditionally-rendered context bar inside. */}
      <div
        data-tour="upload-context"
        role="region"
        aria-label="Upload workflow"
        className="mx-auto max-w-2xl px-4 py-8 md:px-6 space-y-6"
      >

        {/* Upload error banner */}
        {uploadError && (
          <div
            role="alert"
            className={cn(
              'flex items-start gap-2 rounded-lg px-4 py-3 text-sm',
              isDark ? 'bg-red-900/20 text-red-300' : 'bg-red-50 text-red-700',
            )}
          >
            <AlertTriangle className="mt-0.5 h-4 w-4 flex-shrink-0" aria-hidden="true" />
            <span>{uploadError}</span>
          </div>
        )}

        {/* Phase: select */}
        {(phase === 'select') && (
          <>
            <DropZone
              onFilesAccepted={handleFilesAccepted}
              queuedFiles={files}
              onRemoveFile={handleRemoveFile}
              uploading={false}
              isDark={isDark}
            />

            {files.length > 0 && (
              <div className="flex items-center justify-between gap-3">
                <p className={cn('text-xs', isDark ? 'text-neutral-500' : 'text-neutral-500')}>
                  {files.length === 1
                    ? t('upload.file_count', { count: 1 })
                    : t('upload.file_count', { count: files.length })}
                </p>

                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={() => setFiles([])}
                    className={cn(
                      'rounded-lg px-3 py-1.5 text-xs font-medium transition-colors',
                      isDark
                        ? 'text-neutral-400 hover:bg-surface-3 hover:text-neutral-200'
                        : 'text-neutral-500 hover:bg-neutral-100 hover:text-neutral-700',
                    )}
                  >
                    {t('upload.cancel')}
                  </button>

                  <button
                    type="button"
                    onClick={handleStartProcessing}
                    className="rounded-lg bg-brand-600 px-4 py-1.5 text-xs font-semibold text-white transition-colors hover:bg-brand-700 active:bg-brand-700"
                  >
                    {t('upload.start_button')}
                  </button>
                </div>
              </div>
            )}
          </>
        )}

        {/* Phase: uploading — show upload transfer progress */}
        {phase === 'uploading' && (
          <div className="space-y-3">
            <DropZone
              onFilesAccepted={() => {}}
              queuedFiles={files}
              uploading={true}
              isDark={isDark}
            />

            <div className="space-y-1.5">
              <p className={cn('text-sm font-medium', isDark ? 'text-neutral-300' : 'text-neutral-700')}>
                {t('upload.uploading')}
              </p>
              <p className={cn('text-xs', isDark ? 'text-neutral-500' : 'text-neutral-500')}>
                {t('upload.upload_progress', {
                  current: uploadProgress.done,
                  total: uploadProgress.total,
                })}
              </p>
              {/* Transfer progress bar */}
              <div
                role="progressbar"
                aria-valuenow={uploadProgress.total > 0 ? Math.round((uploadProgress.done / uploadProgress.total) * 100) : 0}
                aria-valuemin={0}
                aria-valuemax={100}
                aria-label={t('upload.uploading')}
                className={cn('h-1.5 w-full overflow-hidden rounded-full', isDark ? 'bg-surface-4' : 'bg-neutral-200')}
              >
                <div
                  className="h-full rounded-full bg-brand-500 transition-all duration-300"
                  style={{
                    width: `${uploadProgress.total > 0 ? Math.round((uploadProgress.done / uploadProgress.total) * 100) : 0}%`,
                  }}
                />
              </div>
            </div>
          </div>
        )}

        {/* Phase: processing — SSE batch progress */}
        {(phase === 'processing' || phase === 'done') && batchId && (
          <BatchProgress
            batchId={batchId}
            expectedCount={expectedCount || files.length}
            isDark={isDark}
            onComplete={handleBatchComplete}
          />
        )}

        {/* "Don't wait" — let the user jump to the review queue while items
            are still processing. Items appear in the queue as they reach
            'ready', so the user can start reviewing immediately and stop
            staring at a progress bar. */}
        {phase === 'processing' && batchId && (
          <button
            type="button"
            onClick={() => navigate(`/ingest/batches/${batchId}/review`)}
            className={cn(
              'group flex w-full items-center justify-between gap-3 rounded-xl border px-4 py-3 text-left transition-colors',
              isDark
                ? 'border-white/[0.06] bg-surface-1/60 hover:border-brand-500/30 hover:bg-surface-1'
                : 'border-neutral-200 bg-white hover:border-brand-500/30',
            )}
          >
            <span>
              <span className={cn('block text-sm font-medium', isDark ? 'text-neutral-100' : 'text-neutral-900')}>
                Review the queue while files process
              </span>
              <span className={cn('mt-0.5 block text-xs', isDark ? 'text-neutral-400' : 'text-neutral-600')}>
                Items appear as they reach <strong>ready</strong>. You can start triage in parallel.
              </span>
            </span>
            <ArrowRight
              aria-hidden="true"
              className={cn(
                'h-4 w-4 shrink-0 transition-transform group-hover:translate-x-0.5',
                isDark ? 'text-neutral-500 group-hover:text-brand-300' : 'text-neutral-400 group-hover:text-brand-600',
              )}
            />
          </button>
        )}

        {/* Phase: done — summary + actions. Primary CTA goes to the review
            queue (the actual next step in the pipeline), not back to /. */}
        {phase === 'done' && summary && (
          <div
            className={cn(
              'rounded-xl border p-5 space-y-4',
              isDark ? 'border-white/[0.06] bg-surface-2' : 'border-neutral-200 bg-white',
            )}
          >
            <div className="flex items-center gap-2">
              {summary.failed === 0 ? (
                <CheckCircle2 className="h-5 w-5 text-emerald-400" aria-hidden="true" />
              ) : (
                <AlertTriangle className="h-5 w-5 text-amber-400" aria-hidden="true" />
              )}
              <h2 className={cn('text-sm font-semibold', isDark ? 'text-neutral-200' : 'text-neutral-800')}>
                {summary.failed === 0
                  ? `${summary.ready} ${summary.ready === 1 ? 'document' : 'documents'} ready for review`
                  : `${summary.ready} ready, ${summary.failed} failed`}
              </h2>
            </div>

            <p className={cn('text-xs', isDark ? 'text-neutral-400' : 'text-neutral-600')}>
              Next step: review each item, accept clusters, and commit to your wiki.
            </p>

            <div className="flex flex-wrap gap-2">
              <button
                type="button"
                onClick={() => batchId && navigate(`/ingest/batches/${batchId}/review`)}
                className="inline-flex items-center gap-1.5 rounded-lg bg-brand-600 px-4 py-2 text-xs font-semibold text-white transition-colors hover:bg-brand-700 active:bg-brand-700"
              >
                Review {summary.ready > 0 ? summary.ready : ''} item{summary.ready === 1 ? '' : 's'}
                <ArrowRight className="h-3.5 w-3.5" aria-hidden="true" />
              </button>

              <button
                type="button"
                onClick={handleStartNew}
                className={cn(
                  'rounded-lg border px-4 py-2 text-xs font-medium transition-colors',
                  isDark
                    ? 'border-white/[0.06] text-neutral-300 hover:bg-surface-3'
                    : 'border-neutral-200 text-neutral-700 hover:bg-neutral-100',
                )}
              >
                {t('commit.new_batch')}
              </button>

              <button
                type="button"
                onClick={() => navigate('/')}
                className={cn(
                  'rounded-lg px-3 py-2 text-xs font-medium transition-colors',
                  isDark
                    ? 'text-neutral-500 hover:text-neutral-300'
                    : 'text-neutral-500 hover:text-neutral-700',
                )}
              >
                Back to workspace
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
